<script setup>
import axios from 'axios';
import { onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import PgeButton from '../../../pge-components/pge-button.vue';
import PgeInputText from '../../../pge-components/pge-input-text.vue';
import usePasswordMatch, { PASSWORD_HINT, meetsPasswordRule } from '../../../composables/usePasswordMatch';
import { hubSignInName } from '../../../helpers/hubSignInName';
import Logo from '../../preview/Logo.vue';

defineProps({
	eventData: Object,
});

const router = useRouter();

const params = new URLSearchParams(window.location.search);
const email = ref(params.get('email') || '');
const token = ref(params.get('token') || '');

const password = ref('');
const rePassword = ref('');
const isLoading = ref(false);
const errors = ref([]);
const done = ref(false);
const allowSubmit = ref(false);
// The button stays grey until the two agree; this is what says why.
const { mismatch, onConfirmationFocus } = usePasswordMatch(() => password.value, () => rePassword.value);

const hasValidLink = !!(email.value && token.value);

function validate() {
	allowSubmit.value = meetsPasswordRule(password.value)
		&& password.value === rePassword.value;
}

watch([password, rePassword], validate);

async function handleSubmit() {
	if (!allowSubmit.value || isLoading.value) return;

	isLoading.value = true;
	errors.value = [];

	try {
		await axios.post('/api/sso/api/reset-password', {
			email: email.value,
			token: token.value,
			password: password.value,
			password_confirmation: rePassword.value,
		});
		done.value = true;
		// Password changed → send the member back to the Hub login to sign in.
		router.push('/login');
	} catch (error) {
		errors.value = [error.response?.data?.message || 'Could not reset your password. The link may have expired.'];
	} finally {
		isLoading.value = false;
	}
}

onMounted(() => {
	document.title = `${hubSignInName()} Reset Password`.trim();
});
</script>

<template>
	<div class="reset">
		<div class="reset__image">
			<Logo :logo="eventData.settings.logo" />
		</div>
		<div class="reset__title">Reset your password</div>

		<template v-if="done">
			<div class="reset__message">Your password has been reset. You can now log in with your new password.</div>
			<div class="reset__actions reset__actions--center">
				<pge-button label="Go to login" @click="() => router.push('/login')" />
			</div>
		</template>

		<template v-else-if="!hasValidLink">
			<div class="reset__message">This password reset link is invalid or has expired. Please request a new one.</div>
			<div class="reset__actions reset__actions--center">
				<pge-button label="Back to login" @click="() => router.push('/login')" />
			</div>
		</template>

		<template v-else>
			<div class="bg-white">
				<pge-input-text type="email" name="email" label="Email" :model="email" disabled required
					has-required-sign no-tabindex />
			</div>
			<div class="bg-white">
				<pge-input-text type="password" name="password" label="New Password" :model="password" v-model="password"
					required has-required-sign has-trailing-icon no-tabindex min-length="12"
					:should-show-error="!!password && !meetsPasswordRule(password)" :error-message="PASSWORD_HINT" />
			</div>
			<div class="bg-white">
				<pge-input-text type="password" name="rePassword" label="Confirm New Password" :model="rePassword"
					v-model="rePassword" :submit-on-enter="handleSubmit" required has-required-sign has-trailing-icon
					no-tabindex :has-error="!!mismatch" :error-message="mismatch"
					@update:focused="onConfirmationFocus" />
			</div>
			<div class="reset__actions reset__actions--center">
				<pge-button :label="isLoading ? 'Resetting...' : 'Reset Password'" @click="handleSubmit"
					:disabled="!allowSubmit || isLoading" />
			</div>
			<div class="reset__errors" v-if="errors.length">
				<div v-for="error in errors" :key="error">{{ error }}</div>
			</div>
		</template>
	</div>
</template>

<style lang="scss" scoped>
.reset {
	display: flex;
	flex-direction: column;
	gap: 18px;

	&__image {
		display: flex;
		justify-content: center;
	}

	&__title {
		text-align: center;
		font-weight: 700;
		font-size: 18px;
		line-height: 1.5;
		color: #000;
	}

	&__message {
		font-size: 14px;
		color: #49454f;
		text-align: center;
		line-height: 1.5;
	}

	&__actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;

		&--center {
			justify-content: center;
		}
	}

	&__errors {
		display: flex;
		flex-direction: column;
		gap: 10px;

		div {
			text-align: center;
			color: #f83c3c;
			font-weight: 700;
			font-size: 14px;
			line-height: 20px;
		}
	}
}

.bg-white {
	background: white;
}
</style>
